import { GDL_COOKIES_DOMAIN } from "../utils/cookies";
import { debug, isTrackingEnabled } from "./helpers";
import { getAnonymousUuid, getIdentityUuid } from "./identity";

const PENDO_TOKEN = process.env.NEXT_PUBLIC_PENDO_TOKEN;
const PENDO_ENABLED = !!PENDO_TOKEN;
const SYNC_RETRY_INTERVAL = 50;

export const SYNC_RETRY_ATTEMPT_MAX = 30 * (1000 / SYNC_RETRY_INTERVAL); // Try for 30 seconds

// Null object to be utilized when we don't want to use the real pendo.
const FAKE_PENDO = {
  initialize({ visitor, account }) {
    this.visitor = visitor;
    this.account = account;
  },
  identify({ visitor, account }) {
    this.visitor = visitor;
    this.account = account;
  },
  getVisitorId() {
    return this.visitor && this.visitor.id;
  },
  getAccountId() {
    return this.account && this.account.id;
  },
  isReady() {
    return true;
  },
  loadGuides: () => {},
  // eslint-disable-next-line
  track() {},
};

// The pendo library assigns to window.pendo. This is a local reference to reduce the dependency on `window`.
let pendoInstance;
// Whether the pendo library has been initialized. You should only initialize the library once.
// Even after initializing, pendo isn't guaranteed to be ready until pendo.isReady() is true.
let pendoInitialized = false;
let loadPromise = null;

function getBrowserUuid() {
  return getIdentityUuid() || getAnonymousUuid();
}

function generateAnonymousPendoData() {
  return Promise.resolve({
    visitor: { id: getBrowserUuid(), environment: process.env.NODE_ENV },
    account: null,
  });
}

// Load pendo cdn snippet
function loadPendo() {
  // If it's already been set, return it.
  if (pendoInstance) return Promise.resolve(pendoInstance);

  // If we don't have pendo enabled in this environment, use the FAKE_PENDO object.
  if (!PENDO_ENABLED && isTrackingEnabled()) {
    pendoInstance = FAKE_PENDO;

    return Promise.resolve(pendoInstance);
  }

  const apiKey = PENDO_TOKEN;

  loadPromise =
    loadPromise ||
    new Promise((onSuccess, onFailure) => {
      const onLoad = () => {
        pendoInstance = window.pendo;
        onSuccess(window.pendo);
      };

      // When modifying the install script, snag the script from pendo and add in the
      // following line before the `src` attribute is set.
      //
      // y.onload=onLoad;y.onerror=onFailure;
      //
      // where `y` is the script tag. onLoad and onFailure are variables for the surrounding promise.
      // We're not using the resource loader for this because pendo has page load behavior built into this.
      (function (p, e, n, d, o) {
        let v, w, x, y, z;

        o = p[d] = p[d] || {}; // eslint-disable-line no-multi-assign
        o._q = o._q || [];
        v = ["initialize", "identify", "updateOptions", "pageLoad", "track"];
        for (w = 0, x = v.length; w < x; ++w)
          (function (m) {
            o[m] =
              o[m] ||
              function () {
                o._q[m === v[0] ? "unshift" : "push"]([m].concat([].slice.call(arguments, 0)));
              };
          })(v[w]);
        y = e.createElement(n);
        y.async = !0;
        y.onload = onLoad;
        y.onerror = onFailure;
        y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
        z = e.getElementsByTagName(n)[0];
        z.parentNode.insertBefore(y, z);
      })(window, document, "script", "pendo");
    });

  return loadPromise;
}

// Identify user in pendo with endpoint data
function updatePendo(visitor, account) {
  const data = { visitor };

  if (account) data.account = account;

  // If pendo has been initialized, we need to re-identify and reload the guides.
  if (pendoInitialized) {
    pendoInstance.identify(data);
    pendoInstance.loadGuides();
  } else {
    data.cookieDomain = GDL_COOKIES_DOMAIN;
    pendoInstance.initialize(data);
    pendoInitialized = true;
  }
}

function retrySyncPendo(retriesRemaining) {
  if (!retriesRemaining || retriesRemaining < 0) {
    debug("pendo", "max sync retries reached");

    return;
  }

  setTimeout(() => scheduleSynchronization(retriesRemaining - 1), SYNC_RETRY_INTERVAL);
}

// Schedules an update to be sent to pendo.
export function scheduleSynchronization(retriesRemaining = 0) {
  debug("pendo", "synchronizing");

  if (getBrowserUuid() == null) {
    debug("pendo", "no visitor");

    return;
  }

  Promise.all([generateAnonymousPendoData(), loadPendo()]).then(([{ visitor, account }, pendo]) => {
    const isAvailable = !!pendo?.getVisitorId;
    const isInitializing = isAvailable && pendoInitialized && !pendo.isReady();
    const isReady = isAvailable && pendoInitialized && pendo.isReady();

    if (isReady) {
      // if pendo is fully loaded, sync with it.
      debug("pendo", "updating pendo");
      updatePendo(visitor, account);
    } else if (isInitializing) {
      // if waiting to initialize (usually for document.readyState to change), wait 50ms and retry.
      debug("pendo", "rescheduling, pendo not ready");
      retrySyncPendo(retriesRemaining);
    } else if (isAvailable) {
      // If pendo is available but hasn't been initialized yet, initialize it.
      debug("pendo", "initializing pendo");
      updatePendo(visitor, account);
    } else {
      debug("pendo", "rescheduling, pendo global unavailable");

      // if window.pendo object isn't fully ready, wait 50ms and retry.
      retrySyncPendo(retriesRemaining);
    }
  });
}

export function trackPendoEvent(eventName, properties) {
  const environment = process.env.NEXT_PUBLIC_DEPLOYED_ENV === "production" ? "" : "_not_prod";

  pendoInstance.track(`${eventName}${environment}`, properties);
}
